import * as THREE from "three";
import { Context } from "../core/context";
import { DBEntity, DbLine, DbMesh, FMathUtils, ViewManager } from "@/CadEngine";
import {
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
  ModifyOperateState,
} from "./IModifyOperation";

class BatchRoteModify implements IModifyOperation {
  private m_context: Context;
  private m_points: THREE.Vector3[] = [];
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_auxLine: THREE.Line | undefined;
  private m_selects: DBEntity[] = [];
  private m_curVector: THREE.Vector3 = new THREE.Vector3();
  private m_roteRadian: number = 0;

  constructor(context: Context, rotate: number) {
    this.m_context = context;
    this.m_roteRadian = (rotate / 180) * Math.PI;
    this.m_selects =
      this.m_context.SelectionManager.GetSelected() as DBEntity[];
    this.endHandle();
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_curPoint = point.point.clone();
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {}
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
  }
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: ModifyOperateState) {
    this.clear();
    if (operate == ModifyOperateState.complete) {
      return [];
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  onMouseUp(e: PointerEvent): void {}

  private clear() {}

  private endHandle() {
    let rootIds: string[] = [];
    let rootIdDatas: (DbLine | DbMesh)[] = [];
    let noRootIdDatas: (DbLine | DbMesh)[] = [];
    for (let i = 0; i < this.m_selects.length; i++) {
      let select = this.m_selects[i];
      if (select instanceof DbLine || select instanceof DbMesh) {
        if (select.root_reference_id) {
          if (!rootIds.includes(select.root_reference_id)) {
            rootIds.push(select.root_reference_id);
          }
        } else {
          noRootIdDatas.push(select);
        }
      }
    }
    for (let i = 0; i < rootIds.length; i++) {
      let rootId = rootIds[i];
      let { lines, mesh } = this.m_context.ModelManager.Group[rootId];
      let matrix =
        this.m_context.ModelManager.getInstancesMatrix(rootId)?.matrix;
      if (matrix) {
        let position = new THREE.Vector3();
        let quaternion = new THREE.Quaternion();
        let scale = new THREE.Vector3();
        matrix.decompose(position, quaternion, scale);
        let euler = new THREE.Euler().setFromQuaternion(quaternion);
        let endRotate = this.m_roteRadian + euler.z;
        quaternion.setFromAxisAngle(new THREE.Vector3(0, 0, 1), endRotate);
        matrix.compose(position, quaternion, scale);
        if (lines.length) this.rotateData(position, lines);
        if (mesh.length) this.rotateData(position, mesh);
        rootIdDatas.push(...lines, ...mesh);
      }
    }
    for (let i = 0; i < noRootIdDatas.length; i++) {
      let data = noRootIdDatas[i];
      let position = new THREE.Vector3(
        data.sphere?.center.x,
        data.sphere?.center.y,
        data.sphere?.center.z
      );
      this.rotateData(position, [data]);
    }
    this.m_context.ModelManager.ByDatasUpdateModel(rootIdDatas);
    this.m_context.ModelManager.updateReferencesGeometry(rootIdDatas);
    this.m_context.ModelManager.ByDatasUpdateModel(noRootIdDatas);
    this.m_context.ModifyManager.ModifyFinish(ModifyOperateState.complete);
  }

  private rotateData(basePoint: THREE.Vector3, datas: (DbLine | DbMesh)[]) {
    let zAxis = new THREE.Vector3(0, 0, 1);
    for (let i = 0; i < datas.length; i++) {
      let data = datas[i];
      if (data instanceof DbLine) {
        for (let j = 0; j < data.points.length; j += 3) {
          let v = new THREE.Vector3(
            data.points[j],
            data.points[j + 1],
            data.points[j + 2]
          );
          v.sub(basePoint);
          v.applyAxisAngle(zAxis, this.m_roteRadian);
          v.add(basePoint);
          data.points[j] = v.x;
          data.points[j + 1] = v.y;
          data.points[j + 2] = v.z;
        }
      } else if (data instanceof DbMesh) {
        for (let j = 0; j < data.vertexs.length; j += 3) {
          let v = new THREE.Vector3(
            data.vertexs[j],
            data.vertexs[j + 1],
            data.vertexs[j + 2]
          );
          v.sub(basePoint);
          v.applyAxisAngle(zAxis, this.m_roteRadian);
          v.add(basePoint);
          data.vertexs[j] = v.x;
          data.vertexs[j + 1] = v.y;
          data.vertexs[j + 2] = v.z;
        }
      }
      data.getBoundingBox();
      data.getBoundingSphere();
    }
  }

  private get curDir() {
    return this.m_curPoint.clone().sub(this.m_points[0]);
  }
  private get scale() {
    return this.m_context.ViewManager.ScreenModelScale;
  }
}

export { BatchRoteModify };
